import { useState } from 'react';
import {
  AlertTriangle,
  BarChart3,
  CreditCard,
  Link2,
  Send,
  Sparkles,
  TrendingUp,
  Users,
} from 'lucide-react';
import type { PlanType } from '@app/common';
import { useAdminStats } from '../../hooks/useAdminData';
import { useTranslation } from '../../context/LanguageContext';
import { ACTIVITY_TYPE_KEYS, PLAN_NAME_KEYS, PLATFORM_NAME_KEYS } from '../../i18n';
import type { ActivityType, SocialPlatform } from '@app/common';
import { AdminPanel, AdminStateRow, BarRow, formatNumber } from './AdminShared';

const RANGES = [7, 30, 90] as const;

function StatTile({
  icon: Icon,
  label,
  value,
  gradient,
}: Readonly<{
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  gradient: string;
}>) {
  return (
    <div className="bg-slate-800 rounded-xl p-5 border border-slate-700 shadow-sm">
      <div className="flex items-start justify-between mb-4">
        <p className="text-sm font-semibold text-slate-400 leading-tight">{label}</p>
        <div
          className={`flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br ${gradient} shadow-md flex-shrink-0`}
        >
          <Icon className="h-[18px] w-[18px] text-white" />
        </div>
      </div>
      <p className="text-2xl font-black text-white leading-none">{value}</p>
    </div>
  );
}

export function AdminStatsTab() {
  const { t } = useTranslation();
  const [days, setDays] = useState<number>(30);
  const { data, loading, error } = useAdminStats(days);

  if (loading || error || !data) {
    return (
      <AdminPanel title={t('nav.adminStats')}>
        <AdminStateRow
          loading={loading}
          error={error}
          empty={!data}
          emptyLabel={t('admin.noStats')}
        />
      </AdminPanel>
    );
  }

  const perDayMax = Math.max(1, ...data.generationsPerDay.map((d) => d.count));
  const postsPerDayMax = Math.max(1, ...data.postsPublishedPerDay.map((d) => d.count));
  const planEntries = Object.entries(data.planDistribution) as [PlanType, number][];
  const planMax = Math.max(1, ...planEntries.map(([, n]) => n));
  const platformEntries = (
    Object.entries(data.platformDistribution) as [SocialPlatform, number][]
  ).sort((a, b) => b[1] - a[1]);
  const platformMax = Math.max(1, ...platformEntries.map(([, n]) => n));
  const eventEntries = (Object.entries(data.activityByType) as [ActivityType, number][]).sort(
    (a, b) => b[1] - a[1]
  );
  const eventMax = Math.max(1, ...eventEntries.map(([, n]) => n));

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-1.5">
        {RANGES.map((r) => (
          <button
            key={r}
            type="button"
            onClick={() => setDays(r)}
            className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
              days === r
                ? 'bg-violet-500/20 text-violet-300'
                : 'text-slate-400 hover:bg-slate-700 hover:text-slate-200'
            }`}
          >
            {t('admin.rangeDays', { days: r })}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5">
        <StatTile
          icon={Users}
          label={t('admin.statTotalUsers')}
          value={formatNumber(data.totalUsers)}
          gradient="from-sky-400 to-blue-600"
        />
        <StatTile
          icon={TrendingUp}
          label={t('admin.statNewUsers', { days })}
          value={formatNumber(data.newUsers)}
          gradient="from-emerald-400 to-green-600"
        />
        <StatTile
          icon={CreditCard}
          label={t('admin.statActiveSubs')}
          value={formatNumber(data.activeSubscriptions)}
          gradient="from-violet-400 to-purple-600"
        />
        <StatTile
          icon={BarChart3}
          label={t('admin.statRevenue', { days })}
          value={`৳${formatNumber(data.revenueInPeriod)}`}
          gradient="from-amber-400 to-yellow-500"
        />
        <StatTile
          icon={Send}
          label={t('admin.statPostsPublished')}
          value={formatNumber(data.postsPublishedInPeriod)}
          gradient="from-teal-400 to-cyan-600"
        />
        <StatTile
          icon={Link2}
          label={t('admin.statConnectedAccounts')}
          value={formatNumber(data.connectedAccounts)}
          gradient="from-indigo-400 to-blue-600"
        />
        {/* Silent churn: a brand whose token died has a calendar that has quietly
            stopped publishing, and they may not know. Worth its own tile, and
            worth alerting on when it climbs. */}
        <StatTile
          icon={AlertTriangle}
          label={t('admin.statNeedsReauth')}
          value={formatNumber(data.accountsNeedingReauth)}
          gradient="from-rose-400 to-red-600"
        />
        <StatTile
          icon={Sparkles}
          label={t('admin.statGenerations')}
          value={formatNumber(data.generationsInPeriod)}
          gradient="from-fuchsia-400 to-purple-600"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="space-y-5">
          <AdminPanel title={t('admin.postsPerDayTitle')}>
            <div className="py-2 max-h-80 overflow-y-auto">
              {data.postsPublishedPerDay.map((d) => (
                <BarRow key={d.date} label={d.date} value={d.count} max={postsPerDayMax} />
              ))}
            </div>
          </AdminPanel>

          <AdminPanel title={t('admin.perDayTitle')}>
            <div className="py-2 max-h-80 overflow-y-auto">
              {data.generationsPerDay.map((d) => (
                <BarRow key={d.date} label={d.date} value={d.count} max={perDayMax} />
              ))}
            </div>
          </AdminPanel>
        </div>

        <div className="space-y-5">
          <AdminPanel title={t('admin.platformDistTitle')}>
            <div className="py-2">
              {platformEntries.length === 0 ? (
                <p className="px-5 py-6 text-center text-xs text-slate-500">{t('admin.noStats')}</p>
              ) : (
                platformEntries.map(([platform, count]) => (
                  <BarRow
                    key={platform}
                    label={t(PLATFORM_NAME_KEYS[platform])}
                    value={count}
                    max={platformMax}
                  />
                ))
              )}
            </div>
          </AdminPanel>

          <AdminPanel title={t('admin.planDistTitle')}>
            <div className="py-2">
              {planEntries.length === 0 ? (
                <p className="px-5 py-6 text-center text-xs text-slate-500">{t('admin.noStats')}</p>
              ) : (
                planEntries.map(([plan, count]) => (
                  <BarRow key={plan} label={t(PLAN_NAME_KEYS[plan])} value={count} max={planMax} />
                ))
              )}
            </div>
          </AdminPanel>

          <AdminPanel title={t('admin.eventBreakdownTitle')}>
            <div className="py-2 max-h-64 overflow-y-auto">
              {eventEntries.length === 0 ? (
                <p className="px-5 py-6 text-center text-xs text-slate-500">{t('admin.noStats')}</p>
              ) : (
                eventEntries.map(([type, count]) => (
                  <BarRow
                    key={type}
                    label={t(ACTIVITY_TYPE_KEYS[type])}
                    value={count}
                    max={eventMax}
                  />
                ))
              )}
            </div>
          </AdminPanel>
        </div>
      </div>
    </div>
  );
}
